import { useRef, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import YupPassword from 'yup-password';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Stack, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';
import { facebookPixelId } from 'context/trackingAnalytics/config';

import { FormInput, Info } from 'components';
import { RegisterRequest, TrackingEventsEnum } from 'types';
import { IRegistrationContext } from 'context';
import { useRegistration, useTrackingAnalytics } from 'hooks';
import LockIcon from '@mui/icons-material/Lock';
import { isHipaaLink } from 'config/productProfile';
import { isLettersSpacesTextValid } from 'utils';

import styles from './RegisterForm.module.scss';

declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}

const fbq = window.fbq;

YupPassword(Yup);

// ✅ Initialize Pixel only if this is the top-level page, and the pixel ID is present
if (window === window.parent && typeof fbq === 'function' && facebookPixelId) {
  fbq('init', facebookPixelId);
  fbq('track', 'PageView');
}

export function RegisterForm() {
  const { t, i18n } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { onRegisterFormSubmit: onSubmit }: IRegistrationContext = useRegistration();

  const trackingAnalyticsContext = useTrackingAnalytics();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const location = useLocation();
  const recaptchaLanguage = useMemo(() => {
    const rawLanguage = (i18n.resolvedLanguage || i18n.language || 'en').toLowerCase();

    if (rawLanguage === 'br' || rawLanguage === 'pt' || rawLanguage.startsWith('pt-')) {
      return 'pt-BR';
    }

    if (rawLanguage === 'es' || rawLanguage.startsWith('es-')) {
      return 'es';
    }

    if (rawLanguage === 'fr' || rawLanguage.startsWith('fr-')) {
      return 'fr';
    }

    if (rawLanguage === 'zh' || rawLanguage.startsWith('zh-')) {
      return 'zh-CN';
    }

    if (rawLanguage === 'ru' || rawLanguage.startsWith('ru-')) {
      return 'ru';
    }

    if (rawLanguage === 'ar' || rawLanguage.startsWith('ar-')) {
      return 'ar';
    }

    return 'en';
  }, [i18n.language, i18n.resolvedLanguage]);

  const [formStartTime, setFormStartTime] = useState<number | null>(null); // Track form start time
  const schema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string()
          .label(t('registerFirstName'))
          .trim()
          .test('letters-spaces-only', t('validationLettersSpacesOnly'), (value) => isLettersSpacesTextValid(value))
          .required(t('validationFirstNameRequired')),
        lastName: Yup.string()
          .label(t('registerLastName'))
          .trim()
          .test('letters-spaces-only', t('validationLettersSpacesOnly'), (value) => isLettersSpacesTextValid(value))
          .required(t('validationLastNameRequired')),
        email: Yup.string().label(t('emailLabel')).email(t('validationEmailInvalid')).required(t('validationRequired')),
        organization: Yup.string()
          .label(t('orgName'))
          .min(2, t('validationMin2Chars'))
          .max(100, t('validationMaxLength', { count: 100 }))
          .required(t('validationRequired')),
        password: Yup.string()
          .label(t('passwordLabel'))
          .min(8, t('validationPasswordComplexity'))
          .minLowercase(1, t('validationPasswordLowercase'))
          .minUppercase(1, t('validationPasswordUppercase'))
          .minNumbers(1, t('validationPasswordNumber'))
          .minSymbols(1, t('validationPasswordSymbol'))
          .required(t('validationPasswordComplexity')),
        passwordConfirmation: Yup.string().oneOf([Yup.ref('password')], t('validationPasswordsMustMatch')),
        captcha: Yup.string().required(t('validationCaptchaRequired')),
      }),
    [t],
  );

  const form = useForm<RegisterRequest>({
    resolver: yupResolver(schema),
    mode: 'all', // Fixes mobile first character skipped issue
    defaultValues: {},
  });

  useEffect(() => {
    const handleUserInput = () => {
      if (!formStartTime) {
        setFormStartTime(Date.now());
        console.log('Form start time recorded:', Date.now());
      }
    };

    const inputElements = document.querySelectorAll('input, textarea, select');

    inputElements.forEach((input) => {
      input.addEventListener('focus', handleUserInput);
      input.addEventListener('keydown', handleUserInput);
    });

    return () => {
      inputElements.forEach((input) => {
        input.removeEventListener('focus', handleUserInput);
        input.removeEventListener('keydown', handleUserInput);
      });
    };
  }, [formStartTime]);

  useEffect(() => {
    const isIframe = window !== window.parent;

    if (isIframe) {
      setTimeout(() => {
        const firstInput = document.querySelector('input, textarea') as HTMLInputElement | null;
        if (firstInput) {
          firstInput.focus();
          console.log('Re-focused first input to fix iframe typing bug');
        }
      }, 200);
    }
  }, []);

  useEffect(() => {
    form.setValue('captcha', '');
    recaptchaRef.current?.reset();
  }, [form, recaptchaLanguage]);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    let isIframe = false;

    try {
      isIframe = window.self !== window.top;
    } catch (e) {
      isIframe = true;
    }

    const currentTime = Date.now();
    const trackingSource = isIframe ? 'iframe' : 'main-site';

    if (isIframe) {
      window.parent.postMessage({ fbEvent: 'CompleteRegistration' }, '*');
    }

    if (!formStartTime || currentTime - formStartTime < 10000) {
      enqueueSnackbar(t('wait10Seconds'), { variant: 'error' });
      console.warn('⏳ Submission blocked: Form completed too quickly.');
      setIsSubmitting(false);
      return;
    }

    if (!form.formState.isValid) {
      enqueueSnackbar(t('completeRequiredFields'), { variant: 'error' });
      console.warn('⚠️ Form is invalid, please correct the errors.');
      setIsSubmitting(false);
      return;
    }

    try {
      if (!onSubmit) {
        console.error('❌ Error: onSubmit function is undefined.');
        enqueueSnackbar(t('internalErrorTryLater'), { variant: 'error' });
        return;
      }

      await onSubmit(form.getValues());

      console.log(`✅ Submission successful from: ${trackingSource}`);
      trackingAnalyticsContext.trackEvent(TrackingEventsEnum.CompleteRegistration, {
        email: form.getValues().email,
        source: trackingSource,
      });

      enqueueSnackbar(t('registerSuccess'), { variant: 'success' });
    } catch (error) {
      console.error('❌ Submission error:', error);
      enqueueSnackbar(error instanceof Error ? error.message : t('submissionError'), {
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const onCaptchaSuccess = (token: string | null) => {
    console.log('✅ reCAPTCHA Verified:', token);
    trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Success);

    // ✅ Set the captcha token in the form state
    form.setValue('captcha', token ?? '');
    form.trigger('captcha');
  };

  const onCaptchaError = (error: string) => {
    console.error('❌ reCAPTCHA Failed:', error);
    enqueueSnackbar(t('recaptchaFailed'), { variant: 'error' });

    // ✅ Track reCAPTCHA failure event
    trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Failed, { error });

    // Reset reCAPTCHA to allow retry
    recaptchaRef.current?.reset();
  };

  const showHelp = (title: string, message: string) => {
    alert(`${title}\n\n${message}`);
  };

  return (
    <form onSubmit={form.handleSubmit(handleSubmit)} className={styles.form}>
      <div className={styles.rowWithTwoInputs}>
        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('registerFirstName')} <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput
            name='firstName'
            control={form.control}
            errorHelperClassName={styles.errorMessage}
            autoComplete='off'
          />
        </Stack>

        <Stack className={styles.row}>
          <Stack direction='row' spacing={1} alignItems='center'>
            <Typography component='p' variant='body2'>
              {t('registerLastName')} <span className={styles.asterisk}>*</span>
            </Typography>
          </Stack>
          <FormInput
            name='lastName'
            control={form.control}
            errorHelperClassName={styles.errorMessage}
            autoComplete='off'
          />
        </Stack>
      </div>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('emailLabel')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput name='email' control={form.control} errorHelperClassName={styles.errorMessage} autoComplete='off' />
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {isHipaaLink ? t('providerOrgName') : t('orgName')}
            <span className={styles.asterisk}>*</span>
          </Typography>

          {isHipaaLink && <Info text={t('providerOrgInfo')} />}
        </Stack>

        <FormInput
          name='organization'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          autoComplete='off'
          placeholder={isHipaaLink ? t('orgPlaceholder') : undefined}
          className={styles.inputField}
        />
      </Stack>

      <hr className={styles.divider} />

      <Stack className={styles.row}>
        <Stack direction='row' className={styles.labelWithInfo}>
          <Typography component='p' variant='body2'>
            {t('createPassword')} <span className={styles.asterisk}>*</span>
          </Typography>
          <Info text={t('passwordRequirementInfo')} />
        </Stack>

        <FormInput
          name='password'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          type='password'
          disableError
        />

        {!form.watch('password') && !form.formState.errors.password && (
          <Typography className={styles.passwordRequirement}>{t('passwordRequirementInline')}</Typography>
        )}

        {form.watch('password') && form.formState.errors.password && (
          <Typography className={styles.passwordError}>{form.formState.errors.password.message}</Typography>
        )}
      </Stack>

      <Stack className={styles.row}>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography component='p' variant='body2'>
            {t('confirmPassword')} <span className={styles.asterisk}>*</span>
          </Typography>
        </Stack>
        <FormInput
          name='passwordConfirmation'
          control={form.control}
          errorHelperClassName={styles.errorMessage}
          autoComplete='off'
          type='password'
        />
      </Stack>

      <Stack className={styles.recaptchaContainer} alignItems='center'>
        <div
          onClick={() => {
            console.log('🛡️ reCAPTCHA Clicked');
            trackingAnalyticsContext.trackEvent(TrackingEventsEnum.Recaptcha_Clicked);
          }}
          className={styles.recaptchaWrapper}
        >
          <ReCAPTCHA
            key={recaptchaLanguage}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? ''}
            hl={recaptchaLanguage}
            size='normal'
            ref={recaptchaRef}
            onChange={onCaptchaSuccess}
            onErrored={() => onCaptchaError('Error occurred while loading reCAPTCHA')}
            onExpired={() => onCaptchaError('reCAPTCHA expired, please try again')}
          />
        </div>
      </Stack>

      <Stack className={styles.row} alignItems='flex-end'>
        <Button
          id='RegisterFormNext'
          variant='contained'
          disabled={!form.formState.isValid || isSubmitting}
          className={styles.formButton}
          type='submit'
        >
          {isSubmitting ? t('processing') : t('continue')}
        </Button>
      </Stack>

      <Stack direction='row' alignItems='center' spacing={1} className={styles.secureTextContainer}>
        <LockIcon className={styles.lockIcon} />
        <Typography variant='body2' className={styles.secureText}>
          {t('secureFormText')}
        </Typography>
      </Stack>
    </form>
  );
}

export default RegisterForm;
